import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Edit, Type, Trash2, Plus } from "lucide-react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function EditPdf() {
  const [file, setFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [textToAdd, setTextToAdd] = useState("");
  const [textPosition, setTextPosition] = useState({ x: 50, y: 700 });
  const [fontSize, setFontSize] = useState(14);
  const [textColor, setTextColor] = useState("#000000");
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Edit PDF - Add Text to PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setAnnotations([]);
      await loadPdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setAnnotations([]);
      await loadPdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      
      // Load with pdf-lib for editing
      const doc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      setPdfDoc(doc);
      
      // Load with pdfjs for preview
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const images = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        images.push({
          dataUrl: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height
        });
      }
      
      setPageImages(images);
      setCurrentPage(0);
      toast.success(`PDF loaded: ${pdf.numPages} pages`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const addTextAnnotation = () => {
    if (!textToAdd.trim()) {
      toast.error("Please enter text to add");
      return;
    }
    
    const newAnnotation = {
      id: Date.now(),
      type: 'text',
      page: currentPage,
      text: textToAdd,
      x: textPosition.x,
      y: textPosition.y,
      fontSize: fontSize,
      color: textColor
    };
    
    setAnnotations([...annotations, newAnnotation]);
    setTextToAdd("");
    toast.success("Text annotation added");
  };

  const removeAnnotation = (id) => {
    setAnnotations(annotations.filter(a => a.id !== id));
    toast.success("Annotation removed");
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16) / 255,
      g: parseInt(result[2], 16) / 255,
      b: parseInt(result[3], 16) / 255
    } : { r: 0, g: 0, b: 0 };
  };

  const savePdf = async () => {
    if (!pdfDoc) return;
    
    setIsProcessing(true);
    try {
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const pages = pdfDoc.getPages();
      
      for (const annotation of annotations) {
        if (annotation.type === 'text') {
          const page = pages[annotation.page];
          const { height } = page.getSize();
          const color = hexToRgb(annotation.color);
          
          page.drawText(annotation.text, {
            x: annotation.x,
            y: height - annotation.y,
            size: annotation.fontSize,
            font: font,
            color: rgb(color.r, color.g, color.b)
          });
        }
      }
      
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_edited.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("PDF saved with edits!");
    } catch (error) {
      console.error("Error saving PDF:", error);
      toast.error("Failed to save PDF");
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfDoc(null);
    setPageImages([]);
    setAnnotations([]);
    setCurrentPage(0);
  };

  const pageAnnotations = annotations.filter(a => a.page === currentPage);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-purple-50 to-indigo-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Edit className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full mb-4">
            <Edit className="w-5 h-5" />
            <span className="font-medium">Edit PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Edit PDF - Add Text
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Add text annotations to your PDF documents. Simple and free.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-purple-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF file to edit
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* PDF Preview */}
              <div className="lg:col-span-2">
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold dark:text-white">
                        Page {currentPage + 1} of {pageImages.length}
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                          disabled={currentPage === 0}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.min(pageImages.length - 1, currentPage + 1))}
                          disabled={currentPage === pageImages.length - 1}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                    
                    {isProcessing ? (
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                      </div>
                    ) : (
                      <div className="relative bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                        {pageImages[currentPage] && (
                          <img
                            src={pageImages[currentPage].dataUrl}
                            alt={`Page ${currentPage + 1}`}
                            className="w-full h-auto"
                          />
                        )}
                        {/* Render annotations overlay */}
                        {pageAnnotations.map((ann) => (
                          <div
                            key={ann.id}
                            className="absolute text-xs bg-yellow-200/80 px-1 rounded cursor-pointer hover:bg-yellow-300"
                            style={{
                              left: `${(ann.x / 612) * 100}%`,
                              top: `${(ann.y / 792) * 100}%`,
                              fontSize: `${ann.fontSize * 0.8}px`,
                              color: ann.color
                            }}
                            onClick={() => removeAnnotation(ann.id)}
                            title="Click to remove"
                          >
                            {ann.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Edit Controls */}
              <div className="space-y-4">
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-4 space-y-4">
                    <h3 className="font-semibold dark:text-white flex items-center gap-2">
                      <Type className="w-4 h-4" /> Add Text
                    </h3>
                    
                    <div>
                      <Label className="text-sm">Text</Label>
                      <Input
                        value={textToAdd}
                        onChange={(e) => setTextToAdd(e.target.value)}
                        placeholder="Enter text to add..."
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">X Position</Label>
                        <Input
                          type="number"
                          value={textPosition.x}
                          onChange={(e) => setTextPosition({ ...textPosition, x: parseInt(e.target.value) || 0 })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Y Position</Label>
                        <Input
                          type="number"
                          value={textPosition.y}
                          onChange={(e) => setTextPosition({ ...textPosition, y: parseInt(e.target.value) || 0 })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Font Size</Label>
                        <Input
                          type="number"
                          value={fontSize}
                          onChange={(e) => setFontSize(parseInt(e.target.value) || 14)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Color</Label>
                        <Input
                          type="color"
                          value={textColor}
                          onChange={(e) => setTextColor(e.target.value)}
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                    
                    <Button onClick={addTextAnnotation} className="w-full bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-2" /> Add Text to Page {currentPage + 1}
                    </Button>
                  </CardContent>
                </Card>

                {/* Annotations List */}
                {annotations.length > 0 && (
                  <Card className="dark:bg-slate-800">
                    <CardContent className="p-4">
                      <h3 className="font-semibold dark:text-white mb-3">Annotations ({annotations.length})</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {annotations.map((ann) => (
                          <div
                            key={ann.id}
                            className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-700 rounded text-sm"
                          >
                            <span className="truncate flex-1 dark:text-slate-300">
                              P{ann.page + 1}: {ann.text}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => removeAnnotation(ann.id)}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button onClick={clearFile} variant="outline" className="flex-1">
                    <X className="w-4 h-4 mr-2" /> Clear
                  </Button>
                  <Button 
                    onClick={savePdf} 
                    disabled={isProcessing}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                  >
                    <Download className="w-4 h-4 mr-2" /> Save PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default EditPdf;
